import { parse } from 'marked';
import * as Sentry from '@sentry/browser';

import { secondsToTimestamp } from 'Utils/text';
import { AIChatbotEvents, trackAIChatbotEvent } from 'Analytics/ai-chatbot-events';

const Copy = {
    error: {
        questionResponse: 'We encountered an error. Please try asking your question again.',
    },
};

export default class VuMediChatbotBase {
    constructor(videoSlug = null) {
        this.activeEventSource = null;
        this.brightcovePlayer = null;
        this.chatbotElement = null;
        this.inProgress = false;
        this.uuid = null;
        this.videoSlug = videoSlug;
        this.eventAssistantType = null;
    }

    _handleEventSource(eventSource, answerElement, isPeekaboo = false) {
        return new Promise((resolve) => {
            let processedFirstResponse = false;
            let accumulatedAnswer = '';

            eventSource.addEventListener('message', (ev) => {
                if (isPeekaboo) {
                    // the isPeekaboo is used to report that the user clicked on "See full summary" on the peek-a-boo layout
                    // no need to stream the response again as we already have it
                    eventSource.close();
                    resolve();
                    return;
                }

                try {
                    let initialScrollTop = null;

                    if (!processedFirstResponse) {
                        const messagesElement = this.chatbotElement.querySelector('.js_vmChatbotMessages');
                        initialScrollTop = messagesElement.scrollTop;

                        this._toggleSkeleton(false);
                        processedFirstResponse = true;

                        trackAIChatbotEvent(AIChatbotEvents.RESPONSE_STREAM_STARTED, {
                            assistant_type: this.eventAssistantType,
                        });
                    }

                    const answerData = JSON.parse(ev.data);
                    accumulatedAnswer += answerData.answer;

                    this._answerRenderContent(answerElement, accumulatedAnswer, [], initialScrollTop);

                    if (answerData['last_response']) {
                        const nextBestQuestions = answerData['next_best_questions'] ?? [];

                        this._answerRenderContent(answerElement, accumulatedAnswer, nextBestQuestions);
                        eventSource.close();

                        trackAIChatbotEvent(AIChatbotEvents.RESPONSE_STREAM_FINISHED, {
                            assistant_type: this.eventAssistantType,
                        });

                        resolve();
                    }
                } catch (error) {
                    eventSource.close();
                    this._answerRenderContent(answerElement, Copy.error.questionResponse);

                    trackAIChatbotEvent(AIChatbotEvents.RESPONSE_STREAM_FAILED, {
                        assistant_type: this.eventAssistantType,
                    });

                    Sentry.captureException(error);
                    resolve();
                }
            });

            eventSource.addEventListener('error', (error) => {
                eventSource.close();
                this._answerRenderContent(answerElement, Copy.error.questionResponse);

                trackAIChatbotEvent(AIChatbotEvents.RESPONSE_STREAM_FAILED, {
                    assistant_type: this.eventAssistantType,
                });

                Sentry.captureException(error);
                resolve();
            });
        });
    }

    _answerPrepareElement() {
        const answerElement = document.createElement('div');
        answerElement.classList.add('vm-chatbot-message', 'vm-chatbot-message--answer', 'js_vmChatbotMessage');

        return answerElement;
    }

    _answerPrettifyLinks(links) {
        const prettifyLink = (linkElement) => {
            const hrefAttribute = linkElement.getAttribute('href');

            if (!hrefAttribute) return;

            if (!hrefAttribute.startsWith('vumedi://')) return;

            const linkHref = new URL(hrefAttribute);
            const timestamp = parseInt(linkHref.searchParams.get('timestamp') || '', 10);
            const linkSlug = linkHref.searchParams.get('slug');
            const ictParam = linkHref.searchParams.get('ict');
            const isSameVideo = this.videoSlug && linkSlug === this.videoSlug;

            // Construct URL using current host: /video/{slug}/?t={timestamp}&ict={ict}
            if (linkSlug && linkHref.hostname === 'video.ai') {
                const urlParams = new URLSearchParams();
                if (Number.isInteger(timestamp)) {
                    urlParams.set('t', timestamp.toString());
                }
                if (ictParam) {
                    urlParams.set('ict', ictParam);
                }
                const queryString = urlParams.toString();
                const newHref = `/video/${linkSlug}/${queryString ? '?' + queryString : ''}`;
                linkElement.setAttribute('href', newHref);
            }

            const textElement = document.createElement('span');
            textElement.innerText = linkElement.innerText;
            linkElement.innerText = '';
            linkElement.appendChild(textElement);

            const iconElement = document.createElement('i');
            iconElement.classList.add('icon', 'icon-play-circle');
            linkElement.prepend(iconElement);

            if (Number.isInteger(timestamp)) {
                const timestampElement = document.createElement('strong');
                const timestampValue = secondsToTimestamp(timestamp);

                timestampElement.innerText = `[${timestampValue}]`;
                linkElement.appendChild(timestampElement);
            }

            if (isSameVideo && Number.isInteger(timestamp)) {
                textElement.innerText = '';
            } else {
                linkElement.setAttribute('target', '_blank');
            }

            linkElement.addEventListener('click', (ev) => {
                if (isSameVideo && Number.isInteger(timestamp)) {
                    ev.preventDefault();

                    trackAIChatbotEvent(AIChatbotEvents.VIDEO_SEEKED, {
                        assistant_type: this.eventAssistantType,
                        video_slug: this.videoSlug,
                        timestamp,
                    });

                    this.brightcovePlayer?._player.currentTime(timestamp);
                }

                if (!isSameVideo && linkSlug) {
                    trackAIChatbotEvent(AIChatbotEvents.VIDEO_OPENED, {
                        assistant_type: this.eventAssistantType,
                        video_slug: linkSlug,
                        timestamp: Number.isInteger(timestamp) ? timestamp : null,
                    });
                }

                trackAIChatbotEvent(AIChatbotEvents.URL_SELECTED, {
                    assistant_type: this.eventAssistantType,
                    absolute_url: hrefAttribute,
                });
            });
        };

        links.forEach((el) => prettifyLink(el));
    }

    _answerRenderContent(answerElement, answer, nextBestQuestions = [], presetScrollTop = null) {
        if (!this.chatbotElement) {
            return;
        }

        answerElement.innerHTML = parse(answer);

        this._answerPrettifyLinks(answerElement.querySelectorAll('a'));

        const messagesElement = this.chatbotElement.querySelector('.js_vmChatbotMessages');
        const skeletonElement = this.chatbotElement.querySelector('vm-chatbot-skeleton');
        const scrollTop = presetScrollTop ?? messagesElement.scrollTop;

        messagesElement.insertBefore(answerElement, skeletonElement);

        if (nextBestQuestions?.length) {
            const nextBestQuestionsElement = this._renderNextBestQuestions(nextBestQuestions);
            messagesElement.insertBefore(nextBestQuestionsElement, skeletonElement);
        }

        messagesElement.scrollTop = scrollTop;
    }

    /**
     * The purpose of this method is to dynamically change the height of `.js_vmChatbotSpacer` block element to provide a consistent
     * scrolling experience when using the chatbot in both modal and video assistant modes.
     *
     * The goal of this method is to ensure that the last question can always sit at the top of the viewport in `.vm-chatbot-messages`
     * element, regardless the length of the answer and regardless the height of the skeleton (loading) lines.
     *
     * @param {HTMLElement} containerElement A root chatbot element is needed because when this method is called, class property
     *                                       is still not initialized.
     */
    _dynamicHeightObserver(containerElement) {
        const messagesElement = containerElement.querySelector('.js_vmChatbotMessages');
        const spacerElement = containerElement.querySelector('.js_vmChatbotSpacer');

        const getElementPadding = (element) => {
            try {
                const computedStyle = getComputedStyle(element);
                return parseInt(computedStyle.paddingTop, 10) + parseInt(computedStyle.paddingBottom, 10);
            } catch {
                return 0;
            }
        };

        const getElementMargin = (element) => {
            try {
                const computedStyle = getComputedStyle(element);
                return parseInt(computedStyle.marginBottom, 10);
            } catch {
                return 0;
            }
        };

        const observer = new MutationObserver(() => {
            const messagesContentHeight = messagesElement.offsetHeight - getElementPadding(messagesElement);
            const childrenElements = Array.from(messagesElement.children);

            let childrenHeight = 0;

            while (childrenElements.length) {
                const targetElement = childrenElements.pop();

                if (targetElement.classList.contains('js_vmChatbotSpacer')) {
                    continue;
                }

                childrenHeight += targetElement.offsetHeight + getElementMargin(targetElement);

                if (targetElement.classList.contains('vm-chatbot-message--question')) {
                    break;
                }
            }

            const safeHeight = Math.max(messagesContentHeight - childrenHeight, 0);
            spacerElement.style.setProperty('height', `${safeHeight}px`);
        });

        observer.observe(messagesElement, { childList: true });
    }

    _placeholderSuggestionsClickHandler(parentElement) {
        const placeholderSuggestions = Array.from(parentElement.querySelectorAll('.js_chatbotPlaceholderSuggestion'));
        const suggestionSource = parentElement.getAttribute('data-source') || '';

        placeholderSuggestions.forEach((suggestionElement) => {
            suggestionElement.addEventListener('click', (ev) => {
                ev.preventDefault();

                /* 
                    PEEK-A-BOO SUMMARY CHANGE: the question is added as a data attribute, because we are modifying
                    the "Summarize this video" question text
                */

                const question = ev.target.dataset.question || ev.target.innerText.trim();
                this._askQuestion(question, ev.target.dataset.type, suggestionSource);
            });
        });
    }

    _renderNextBestQuestions(nextBestQuestions) {
        const nbqContainer = document.createElement('div');
        nbqContainer.classList.add(
            'vm-chatbot-message',
            'vm-chatbot-message--answer',
            'vm-chatbot-message--nbq',
            'js_vmChatbotMessage',
        );

        const nbqHeading = document.createElement('h3');
        nbqHeading.innerText = 'Follow-up questions...';

        nbqContainer.appendChild(nbqHeading);

        nextBestQuestions.forEach((question) => {
            const questionElement = document.createElement('button');

            questionElement.setAttribute('type', 'button');
            questionElement.classList.add('vm-button', 'vm-button--chatbot-suggestion');
            questionElement.innerText = question.question;

            questionElement.addEventListener('click', (ev) => {
                ev.preventDefault();
                this._askQuestion(question.question, question.type);
            });

            nbqContainer.appendChild(questionElement);
        });

        return nbqContainer;
    }

    _renderQuestion(question) {
        if (!this.chatbotElement) {
            return;
        }

        const questionElement = document.createElement('div');
        const skeletonElement = this.chatbotElement.querySelector('vm-chatbot-skeleton');
        questionElement.classList.add('vm-chatbot-message', 'vm-chatbot-message--question');
        questionElement.innerText = question;

        const iconElement = document.createElement('i');
        iconElement.classList.add('icon', 'icon-chatbot');
        questionElement.prepend(iconElement);

        const messagesElement = this.chatbotElement.querySelector('.js_vmChatbotMessages');
        messagesElement.insertBefore(questionElement, skeletonElement);

        const scrollOffset = 15;

        setTimeout(() => {
            messagesElement.scrollTo({
                top: questionElement.offsetTop - scrollOffset,
                behavior: 'smooth',
            });
        }, 0);
    }

    _getSuggestionMarkup(id, type, question, additionalClassName = '', fadeInGroup = null, elementType = '') {
        /*
            PEEK-A-BOO SUMMARY CHANGE
            elementType is added because in the "Summarize this video" question we show the answer content, 
            so to be semantically correct, we use a <div> element instead of a <button>
        */

        const fadeInMarkup = fadeInGroup !== null ? `data-fadein-group="${fadeInGroup}"` : '';

        if (elementType) {
            return `<${elementType}
                id="${id}"
                class="vm-button vm-button--chatbot-suggestion ${additionalClassName}"
                data-type="${type}"
                data-question="${question}"
                ${fadeInMarkup}
            >
                ${question}
            </${elementType}>`;
        }

        return `<button
            type="button"
            id="${id}"
            class="vm-button vm-button--chatbot-suggestion ${additionalClassName}"
            data-type="${type}"
            ${fadeInMarkup}
        >
            ${question}
        </button>`;
    }

    _vumediAiLogo() {
        const metaElement = document.querySelector(`meta[name="vumedi-ai-logo"]`);
        const logoSource = metaElement?.content;

        return `<img class="vm-chatbot-header__logo" src="${logoSource}">`;
    }

    _toggleSkeleton(show = true) {
        const target = this.chatbotElement.querySelector('vm-chatbot-skeleton');

        if (show) {
            target.removeAttribute('data-hidden');
        } else {
            target.setAttribute('data-hidden', '');
        }
    }
}
